/**
 * Генеративный фон на WebAudio: 0 байт загрузки, бесконечный без швов.
 * drone — мягкий аккорд с медленным «дыханием» громкости поверх коричневого шума.
 * rain — отфильтрованный шум с лёгкой нерегулярностью.
 */
export type AmbientKind = "none" | "drone" | "rain";

export interface Ambient {
  output: GainNode;
  stop(fadeSec?: number): void;
}

function noiseBuffer(ctx: BaseAudioContext, seconds: number, color: "brown" | "pink"): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    let last = 0;
    let b0 = 0,
      b1 = 0,
      b2 = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      if (color === "brown") {
        last = (last + 0.02 * w) / 1.02;
        d[i] = last * 3.5;
      } else {
        b0 = 0.99765 * b0 + w * 0.099046;
        b1 = 0.963 * b1 + w * 0.2965164;
        b2 = 0.57 * b2 + w * 1.0526913;
        d[i] = (b0 + b1 + b2 + w * 0.1848) * 0.11;
      }
    }
    // плавная склейка петли
    const fade = Math.floor(ctx.sampleRate * 0.05);
    for (let i = 0; i < fade; i++) {
      const k = i / fade;
      d[i] = d[i]! * k + d[len - fade + i]! * (1 - k);
    }
  }
  return buf;
}

function lfo(ctx: AudioContext, target: AudioParam, freq: number, depth: number) {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.frequency.value = freq;
  g.gain.value = depth;
  osc.connect(g).connect(target);
  osc.start();
  return osc;
}

export function createAmbient(ctx: AudioContext, kind: AmbientKind, volume: number): Ambient | null {
  if (kind === "none") return null;
  const out = ctx.createGain();
  out.gain.value = 0;
  out.gain.setTargetAtTime(volume, ctx.currentTime, 2.5); // медленный вход
  const nodes: AudioScheduledSourceNode[] = [];

  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuffer(ctx, 6, kind === "drone" ? "brown" : "pink");
  noise.loop = true;
  const nf = ctx.createBiquadFilter();
  const ng = ctx.createGain();

  if (kind === "drone") {
    nf.type = "lowpass";
    nf.frequency.value = 420;
    ng.gain.value = 0.35;
    // A2–E3–A3–C#4: открытый, «тёплый» аккорд
    const tones = [110, 164.81, 220, 277.18];
    const chordBus = ctx.createGain();
    chordBus.gain.value = 0.11;
    const soft = ctx.createBiquadFilter();
    soft.type = "lowpass";
    soft.frequency.value = 900;
    chordBus.connect(soft).connect(out);
    tones.forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = i % 2 ? "triangle" : "sine";
      o.frequency.value = f;
      o.detune.value = (i - 1.5) * 4;
      const g = ctx.createGain();
      g.gain.value = 0.5 / (i + 1);
      nodes.push(lfo(ctx, g.gain, 0.03 + i * 0.017, 0.18 / (i + 1)));
      o.connect(g).connect(chordBus);
      o.start();
      nodes.push(o);
    });
    nodes.push(lfo(ctx, nf.frequency, 0.05, 140));
  } else {
    nf.type = "bandpass";
    nf.frequency.value = 2400;
    nf.Q.value = 0.4;
    ng.gain.value = 0.9;
    nodes.push(lfo(ctx, ng.gain, 0.21, 0.12));
    nodes.push(lfo(ctx, nf.frequency, 0.07, 700));
  }
  noise.connect(nf).connect(ng).connect(out);
  noise.start();
  nodes.push(noise);

  return {
    output: out,
    stop(fadeSec = 1.5) {
      const t = ctx.currentTime;
      out.gain.cancelScheduledValues(t);
      out.gain.setValueAtTime(out.gain.value, t);
      out.gain.linearRampToValueAtTime(0, t + fadeSec);
      nodes.forEach((n) => {
        try {
          n.stop(t + fadeSec + 0.1);
        } catch {
          /* уже остановлен */
        }
      });
    },
  };
}
